import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProfileTier } from '@fcom/core-api/login';
import { ButtonTheme, IconPosition } from '@fcom/ui-components';
import { MediaQueryService, LoyaltyTierService, TierState, MemberTierDetails } from '@fcom/common';
import { finShare } from '@fcom/rx';

export type TierProgressData = Pick<
  MemberTierDetails,
  | 'tierName'
  | 'tierState'
  | 'isJuniorMember'
  | 'isStartingTier'
  | 'isGainingMaintainingOrHighestLumo'
  | 'targetTier'
  | 'targetTierName'
  | 'trackingPeriodStart'
  | 'trackingPeriodEnd'
  | 'cardExpirationDate'
  | 'targetTierLimit'
  | 'aySecretTierPointsRequired'
  | 'aySecretTierPoints'
  | 'tierPointsCollected'
  | 'targetTierFlightLimit'
  | 'qualifyingFlights'
  | 'tierProgress'
>;

export type LifetimeProgressData = Pick<
  MemberTierDetails,
  'lifetimeTier' | 'lifetimeTargetTier' | 'lifetimeTargetTierLimit' | 'qualifyingLifetimeTierPoints'
>;

@Component({
  selector: 'fin-tier-progress',
  templateUrl: 'tier-progress.component.html',
  styleUrls: ['./tier-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TierProgressComponent implements OnInit {
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly ProfileTier = ProfileTier;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TierState = TierState;

  tierProgressData$: Observable<TierProgressData>;
  lifetimeProgressData$: Observable<LifetimeProgressData>;

  isMobile$: Observable<boolean>;
  showLifetime$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private mediaQueryService: MediaQueryService,
    private loyaltyTierService: LoyaltyTierService
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');

    const getMemberTierDetails$ = this.loyaltyTierService.getMemberTierDetails().pipe(finShare());

    this.tierProgressData$ = getMemberTierDetails$.pipe(
      map((memberTierDetails) => {
        return {
          tierName: memberTierDetails.tierName,
          tierState: memberTierDetails.tierState,
          isJuniorMember: memberTierDetails.isJuniorMember,
          isStartingTier: memberTierDetails.isStartingTier,
          isGainingMaintainingOrHighestLumo: memberTierDetails.isGainingMaintainingOrHighestLumo,
          targetTier: memberTierDetails.targetTier,
          targetTierName: memberTierDetails.targetTierName,
          cardExpirationDate: memberTierDetails.cardExpirationDate,
          trackingPeriodStart: memberTierDetails.trackingPeriodStart,
          trackingPeriodEnd: memberTierDetails.trackingPeriodEnd,
          targetTierLimit: memberTierDetails.targetTierLimit,
          aySecretTierPointsRequired: memberTierDetails.aySecretTierPointsRequired,
          aySecretTierPoints: memberTierDetails.aySecretTierPoints,
          tierPointsCollected: memberTierDetails.tierPointsCollected,
          targetTierFlightLimit: memberTierDetails.targetTierFlightLimit,
          qualifyingFlights: memberTierDetails.qualifyingFlights,
          tierProgress: memberTierDetails.tierProgress,
        };
      }),
      finShare()
    );

    this.lifetimeProgressData$ = getMemberTierDetails$.pipe(
      map((memberTierDetails) => {
        return {
          lifetimeTier: memberTierDetails.lifetimeTier,
          lifetimeTargetTier: memberTierDetails.lifetimeTargetTier,
          lifetimeTargetTierLimit: memberTierDetails.lifetimeTargetTierLimit,
          qualifyingLifetimeTierPoints: memberTierDetails.qualifyingLifetimeTierPoints,
        };
      }),
      finShare()
    );
  }
}
